import React from "react";
import { Helmet } from "react-helmet";
import { AiFillPhone } from "react-icons/ai";
import { MdEmail, MdSms } from "react-icons/md";

export default function Contact() {
  return (
    <div className="font-nunito">
      <Helmet>
        <title>Contact | Pathway Resource Center</title>
        <meta
          name="description"
          content="Get in touch with a member of Pathway Resource Center for information about free pregnancy services, volunteering, or upcoming community events."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-pink-50 py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-[#457B9D]"></div>
          <div className="absolute -right-32 top-40 h-80 w-80 rounded-full bg-pink-300"></div>
          <div className="absolute -bottom-20 left-1/3 h-72 w-72 rounded-full bg-blue-200"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-4">
          <div className="text-center">
            <h1 className="mb-6 text-5xl font-bold text-gray-800 md:text-6xl">
              We're Here to Help
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600">
              You don't have to walk this path alone. Whether you have questions
              about pregnancy, want to volunteer, or need support—we're ready to
              listen.
            </p>
          </div>

          {/* Quick Contact Cards */}
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-3">
                <AiFillPhone className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-800">Call Us</h3>
              <a
                href="tel:+18707792229"
                className="text-xl font-bold text-[#457B9D] transition-colors hover:text-[#365f7a]"
              >
                870-779-2229
              </a>
              <p className="mt-2 text-sm text-gray-600">Available during business hours</p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-3">
                <MdSms className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-800">Text Us</h3>
              <a
                href="sms:+18707792229"
                className="text-xl font-bold text-[#457B9D] transition-colors hover:text-[#365f7a]"
              >
                870-779-2229
              </a>
              <p className="mt-2 text-sm text-gray-600">Quick and private support</p>
            </div>

            <div className="group rounded-2xl bg-white p-6 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="mb-4 inline-flex rounded-full bg-blue-100 p-3">
                <MdEmail className="text-2xl text-[#457B9D]" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-800">Email Us</h3>
              <p className="text-lg font-medium text-gray-700">Use the form below</p>
              <p className="mt-2 text-sm text-gray-600">We'll respond within 24 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-gradient-to-b from-white to-blue-50 py-16">
        <div className="mx-auto max-w-3xl px-4">
          <div className="rounded-3xl bg-white p-8 shadow-xl md:p-12">
            <div className="mb-8 text-center">
              <h2 className="mb-3 text-3xl font-bold text-gray-800 md:text-4xl">
                Send Us a Message
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <form
              action="https://formspree.io/f/mnqrygqb"
              method="POST"
              className="space-y-6"
            >
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-gray-700"
                  >
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 transition-all duration-200 placeholder:text-gray-400 focus:border-[#457B9D] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#457B9D] focus:ring-opacity-20"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="mb-2 block text-sm font-semibold text-gray-700"
                  >
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 transition-all duration-200 placeholder:text-gray-400 focus:border-[#457B9D] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#457B9D] focus:ring-opacity-20"
                    placeholder="870-123-4567"
                    pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact_reason"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  How Can We Help You? <span className="text-red-500">*</span>
                </label>
                <select
                  id="contact_reason"
                  name="contact_reason"
                  className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 transition-all duration-200 focus:border-[#457B9D] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#457B9D] focus:ring-opacity-20"
                  required
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select a reason for contact
                  </option>
                  <option value="pregnant">
                    I Need Pregnancy Support or Services
                  </option>
                  <option value="volunteer">
                    I Want to Volunteer
                  </option>
                  <option value="donation">
                    I'd Like to Make a Donation
                  </option>
                  <option value="general">
                    General Question or Information
                  </option>
                  <option value="other">
                    Other
                  </option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 transition-all duration-200 placeholder:text-gray-400 focus:border-[#457B9D] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#457B9D] focus:ring-opacity-20"
                  placeholder="Tell us how we can help you..."
                  required
                ></textarea>
              </div>

              <div className="rounded-xl bg-blue-50 p-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Privacy Note:</span> All information
                  you share with us is completely confidential and will only be used to
                  respond to your inquiry.
                </p>
              </div>

              <button
                type="submit"
                className="group w-full rounded-xl bg-[#457B9D] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:bg-[#365f7a] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#457B9D] focus:ring-opacity-30 md:w-auto"
              >
                <span className="flex items-center justify-center gap-2">
                  Send Message
                  <svg
                    className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </span>
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Emergency Support Banner */}
      <section className="bg-gradient-to-r from-[#457B9D] to-[#365f7a] py-12">
        <div className="mx-auto max-w-4xl px-4 text-center text-white">
          <h3 className="mb-4 text-2xl font-bold md:text-3xl">
            Need Immediate Assistance?
          </h3>
          <p className="mb-6 text-lg opacity-90">
            If you need urgent support, please don't hesitate to call or text us right
            away.
          </p>
          <a
            href="tel:+18707792229"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-bold text-[#457B9D] shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <AiFillPhone className="text-xl" />
            Call Now: 870-779-2229
          </a>
        </div>
      </section>
    </div>
  );
}
